import { useCallback, useEffect, useRef } from 'react';

import { getAllFieldsTouched } from '../../../../../common';
import { toAssessmentMethodVisualFieldPayload } from '../utils';
import { ASSESSMENT_METHOD_VISUAL_FORM_SCHEMA } from '../validationSchema';

import type { SaveField } from '../../../../../common';
import type { AssessmentMethodVisualFormValues } from '../types';
import type { SaveAssessmentMethodVisualParams } from './types';

export default function useSaveAssessmentMethodVisual({
  methodId,
  visualId,
  pendingFile,
  formik,
  initialValues,
  onCreate,
  onUpdate,
  onUpdateFile,
  onCreated,
}: SaveAssessmentMethodVisualParams) {
  const savedFieldValuesRef = useRef<AssessmentMethodVisualFormValues>({ ...initialValues });
  const visualIdRef = useRef(visualId);
  const savingPromiseRef = useRef(Promise.resolve());

  useEffect(() => {
    savedFieldValuesRef.current = { ...initialValues };
  }, [initialValues]);

  useEffect(() => {
    visualIdRef.current = visualId;
  }, [visualId]);

  const saveField: SaveField<AssessmentMethodVisualFormValues> = useCallback(
    async (name, value) => {
      const values = value === undefined ? formik.values : { ...formik.values, [name]: value };

      try {
        await ASSESSMENT_METHOD_VISUAL_FORM_SCHEMA.validateAt(name, values);
      } catch {
        return;
      }

      const currentSave = savingPromiseRef.current
        .catch(() => undefined)
        .then(async () => {
          if (values[name] === savedFieldValuesRef.current[name]) {
            return;
          }

          const currentId = visualIdRef.current;
          if (currentId == null) {
            if (!pendingFile) return;
            try {
              await ASSESSMENT_METHOD_VISUAL_FORM_SCHEMA.validate(values);
            } catch {
              return;
            }
            const createdId = await onCreate(methodId, pendingFile, {
              title: values.title,
              analysis: values.analysis,
            });
            if (createdId == null) return;
            visualIdRef.current = createdId;
            savedFieldValuesRef.current = { ...values };
            await formik.setTouched(getAllFieldsTouched(values), true);
            onCreated(createdId);
            return;
          }

          const didUpdate = await onUpdate(
            methodId,
            currentId,
            toAssessmentMethodVisualFieldPayload(name, values),
          );
          if (!didUpdate) return;
          savedFieldValuesRef.current = { ...savedFieldValuesRef.current, [name]: values[name] };
          await formik.setTouched(getAllFieldsTouched(values), true);
        });

      savingPromiseRef.current = currentSave;
      await currentSave;
    },
    [formik, methodId, onCreate, onCreated, onUpdate, pendingFile],
  );

  const saveFile = useCallback(
    async (file: File) => {
      let saved = false;
      const currentSave = savingPromiseRef.current
        .catch(() => undefined)
        .then(async () => {
          const currentId = visualIdRef.current;
          if (currentId == null) return;
          saved = await onUpdateFile(methodId, currentId, file);
        });
      savingPromiseRef.current = currentSave;
      await currentSave;
      return saved;
    },
    [methodId, onUpdateFile],
  );

  return { saveField, saveFile };
}
